import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface MenuButtonProps {
  isHome: boolean;
  isScrolled: boolean;
}

const MenuButton = ({ isHome, isScrolled }: MenuButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scroll position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Determine icon color based on page and scroll state
  const getIconColor = () => {
    // For mobile, use white when scrolled
    const isMobile = window.innerWidth < 768;
    if (isMobile && isScrolled) {
      return 'text-white';
    }
    
    if (isHome && !isScrolled) {
      return 'text-white';
    }
    return 'text-gray-700';
  };

  return (
    <>
      {!isOpen && (
      <button 
          className={`${getIconColor()} transition-all duration-300 text-lg md:text-xl p-2 relative group`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
        style={{ 
          padding: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '44px',
            minHeight: '44px',
            position: 'relative',
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <i className="fa-solid fa-bars relative z-10"></i>
          {/* Animated background circle */}
          <span 
            className="absolute inset-0 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300"
            style={{
              backgroundColor: 'rgba(255, 186, 23, 0.1)',
              border: '2px solid rgba(255, 186, 23, 0.3)',
            }}
          />
      </button>
      )}

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #0f1b3c 0%, #1a2d5c 50%, #0f1b3c 100%)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.2s ease-out',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
          onClick={(e) => {
            // Close menu if clicking on the overlay background
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          {/* Animated background patterns */}
          <div 
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.1,
              background: `
                radial-gradient(circle at 20% 30%, rgba(255, 186, 23, 0.3) 0%, transparent 40%),
                radial-gradient(circle at 80% 70%, rgba(255, 186, 23, 0.2) 0%, transparent 50%)
              `,
              pointerEvents: 'none',
            }}
          />
          
          {/* Close Button */}
          <button 
            className="absolute text-white transition-all duration-300 p-2 group z-50"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
            style={{
              position: 'fixed',
              top: '1rem',
              right: '1rem',
              color: 'white',
              fontSize: '1.75rem',
              transition: 'all 0.3s ease',
              padding: '0.75rem',
              borderRadius: '50%',
              border: '2px solid rgba(255, 186, 23, 0.3)',
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 186, 23, 0.2)';
              e.currentTarget.style.transform = 'rotate(90deg) scale(1.1)';
              e.currentTarget.style.borderColor = '#ffba17';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
              e.currentTarget.style.borderColor = 'rgba(255, 186, 23, 0.3)';
            }}
          >
            <i className="fa-solid fa-times"></i>
          </button>

          {/* Menu Content */}
          <div 
            className="w-full max-w-6xl px-6 md:px-8 flex flex-col md:flex-row space-y-16 md:space-y-0 md:space-x-16 py-12 md:py-20 relative z-10"
            style={{
              maxHeight: '100vh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {/* Left Column - Navigation */}
            <div className="w-full md:w-1/2 md:pr-16" style={{ paddingTop: '3rem' }}>
              <nav className="text-center md:text-left">
                <ul className="space-y-6 md:space-y-8">
                  {[
                    { name: 'Home', path: '/', icon: 'fa-home' },
                    { name: 'About Us', path: '/about', icon: 'fa-info-circle' },
                    { name: 'Categories', path: '/categories', icon: 'fa-th-large' },
                    { name: 'Winners', path: '/winners', icon: 'fa-trophy' },
                    { name: 'Contact Us', path: '/contact', icon: 'fa-envelope' },
                    { name: 'Nominate', path: '/nominate', icon: 'fa-star' },
                    { name: 'Login', path: 'https://vote.digitalawards.co.tz', external: true, icon: 'fa-sign-in-alt' }
                  ].map((item, index) => (
                    <li 
                      key={index} 
                      className="group"
                      style={{
                        animation: `slideInLeft 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      {item.external ? (
                        <a 
                          href={item.path} 
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsOpen(false)} 
                          className="relative inline-flex items-center gap-4 text-white text-2xl md:text-4xl font-bold uppercase tracking-wider transition-all duration-300"
                          style={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '1rem',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ffba17';
                            e.currentTarget.style.transform = 'translateX(10px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          <span 
                            className="w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center"
                            style={{
                              backgroundColor: 'rgba(255, 186, 23, 0.2)',
                              border: '2px solid rgba(255, 186, 23, 0.3)',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <i className={`fa-solid ${item.icon} text-sm md:text-lg`} style={{ color: '#ffba17' }}></i>
                          </span>
                          <span className="relative">
                          {item.name}
                            <span 
                              className="absolute bottom-0 left-0 w-0 h-1 transition-all duration-300 group-hover:w-full"
                              style={{ 
                                backgroundColor: '#ffba17',
                                height: '3px',
                              }}
                            />
                          </span>
                        </a>
                      ) : (
                        <Link 
                          to={item.path} 
                          onClick={() => setIsOpen(false)} 
                          className="relative inline-flex items-center gap-4 text-white text-2xl md:text-4xl font-bold uppercase tracking-wider transition-all duration-300"
                          style={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '1rem',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ffba17';
                            e.currentTarget.style.transform = 'translateX(10px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          <span 
                            className="w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center"
                            style={{
                              backgroundColor: 'rgba(255, 186, 23, 0.2)',
                              border: '2px solid rgba(255, 186, 23, 0.3)',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <i className={`fa-solid ${item.icon} text-sm md:text-lg`} style={{ color: '#ffba17' }}></i>
                          </span>
                          <span className="relative">
                          {item.name}
                            <span 
                              className="absolute bottom-0 left-0 w-0 h-1 transition-all duration-300 group-hover:w-full"
                              style={{ 
                                backgroundColor: '#ffba17',
                                height: '3px',
                              }}
                            />
                          </span>
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Right Column - Contact Info */}
            {/* <div className="w-full md:w-1/2 md:pl-16 flex flex-col justify-center space-y-12 md:space-y-12">
             
              <div className="text-center md:text-left">
                <h3 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider mb-6 md:mb-6">Our Office</h3>
                <p className="text-gray-300 text-lg md:text-xl leading-relaxed">
                  8th Floor Victoria House<br />
                  Mikocheni, Dar es Salaam, Tanzania.
                </p>
              </div>

             
              <div className="text-center md:text-left">
                <h3 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider mb-6 md:mb-6">Get in Touch</h3>
                <div className="space-y-3">
                  <p className="text-gray-300 text-lg md:text-xl leading-relaxed">
                    info@digitalawards.co.tz
                  </p>
                  <p className="text-gray-300 text-lg md:text-xl leading-relaxed">
                    +255 737 957082
                  </p>
                </div>
              </div>

          
              <div className="flex justify-center md:justify-start space-x-6 md:space-x-6">
                {[
                  { icon: 'fa-brands fa-facebook-f', url: 'https://www.facebook.com/digitalawardstz' },
                  { icon: 'fa-brands fa-x-twitter', url: 'https://x.com/digitalawardstz' },
                  { icon: 'fa-brands fa-instagram', url: 'https://www.instagram.com/digitalawardstz' },
                  { icon: 'fa-brands fa-youtube', url: 'https://www.youtube.com/digitalawardstz' }
                ].map((social, index) => (
                  <a 
                    key={index}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-14 h-14 md:w-14 md:h-14 border-2 border-white text-white rounded-full flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-110"
                  >
                    <i className={`${social.icon} text-lg md:text-xl`}></i>
                  </a>
                ))}
              </div>
            </div> */}
          </div>
        </div>
      )}
    </>
  );
};

export default MenuButton;
